import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ConnectionStatus } from '../../types/connections';
import { Pipeline } from '../../types/pipelines';
import { apiFetch } from '../../services/api';
import { PipelineFormLayout } from '../PipelineFormLayout';

const MarketingAttributionForm: React.FC = () => {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const isEditing = !!id;

    const [pipelineName, setPipelineName] = useState('');
    const [selectedGBQConnection, setSelectedGBQConnection] = useState<string>('');
    const [selectedGoogleAdsConnection, setSelectedGoogleAdsConnection] = useState<string>('');
    const [attributionWindow, setAttributionWindow] = useState<number>(30);
    const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus | null>(null);
    
    // BigQuery State
    const [bqProjects, setBqProjects] = useState<{ id: string; name: string }[]>([]);
    const [selectedProject, setSelectedProject] = useState<string>('');
    const [loadingBQ, setLoadingBQ] = useState(false);
    const [bqError, setBqError] = useState<string | null>(null);
    
    // GA4 raw export State
    const [ga4ExportProjects, setGa4ExportProjects] = useState<{ id: string; name: string }[]>([]);
    const [selectedGa4ExportProject, setSelectedGa4ExportProject] = useState<string>('');
    const [ga4ExportDatasets, setGa4ExportDatasets] = useState<string[]>([]);
    const [selectedGa4ExportDataset, setSelectedGa4ExportDataset] = useState<string>('');
    const [loadingGa4Export, setLoadingGa4Export] = useState(false);
    
    // Google Ads State
    const [googleAdsProjects, setGoogleAdsProjects] = useState<{ id: string; name: string }[]>([]);
    const [selectedGoogleAdsProject, setSelectedGoogleAdsProject] = useState<string>('');
    const [googleAdsDatasets, setGoogleAdsDatasets] = useState<string[]>([]);
    const [selectedGoogleAdsDataset, setSelectedGoogleAdsDataset] = useState<string>('');
    const [loadingGoogleAds, setLoadingGoogleAds] = useState(false);
    
    // Meta Ads State
    const [metaAdsProjects, setMetaAdsProjects] = useState<{ id: string; name: string }[]>([]);
    const [selectedMetaAdsProject, setSelectedMetaAdsProject] = useState<string>('');
    const [metaAdsDatasets, setMetaAdsDatasets] = useState<string[]>([]);
    const [selectedMetaAdsDataset, setSelectedMetaAdsDataset] = useState<string>('');
    const [metaAdsTables, setMetaAdsTables] = useState<string[]>([]);
    const [selectedMetaAdsTable, setSelectedMetaAdsTable] = useState<string>('');
    const [loadingMetaAds, setLoadingMetaAds] = useState(false);
    
    // CRM State
    const [crmProjects, setCrmProjects] = useState<{ id: string; name: string }[]>([]);
    const [selectedCrmProject, setSelectedCrmProject] = useState<string>('');
    const [crmDatasets, setCrmDatasets] = useState<string[]>([]);
    const [selectedCrmDataset, setSelectedCrmDataset] = useState<string>('');
    const [crmTables, setCrmTables] = useState<string[]>([]);
    const [selectedCrmTable, setSelectedCrmTable] = useState<string>('');
    const [loadingCrm, setLoadingCrm] = useState(false);

    useEffect(() => {
        loadConnectionStatus();
        if (isEditing) {
            loadPipeline(id!);
        }
    }, [id, isEditing]);

    const loadConnectionStatus = async () => {
        try {
            const res = await apiFetch('/api/connections/status');
            if (res.ok) {
                setConnectionStatus(await res.json());
            }
        } catch (error) {
            console.error('Failed to load connection status:', error);
        }
    };

    const loadPipeline = async (pipelineId: string) => {
        try {
            const res = await apiFetch(`/api/pipelines/${pipelineId}`);
            if (res.ok) {
                const pipeline: Pipeline = await res.json();
                setPipelineName(pipeline.name);
                if (pipeline.connections?.gbqConnectionId) setSelectedGBQConnection(pipeline.connections.gbqConnectionId);
                if (pipeline.connections?.gadsConnectionId) setSelectedGoogleAdsConnection(pipeline.connections.gadsConnectionId);
                if (pipeline.parameters?.attributionWindow) setAttributionWindow(pipeline.parameters.attributionWindow);
                
                const dataSources = pipeline.dataSources as any;
                if (dataSources?.marketing_attribution) {
                    const ma = dataSources.marketing_attribution;
                    // Load BigQuery Connector project
                    if (ma.bigqueryConnector?.project) {
                        setSelectedProject(ma.bigqueryConnector.project);
                    }
                    if (ma.ga4Export?.project) setSelectedGa4ExportProject(ma.ga4Export.project);
                    if (ma.ga4Export?.dataset) setSelectedGa4ExportDataset(ma.ga4Export.dataset);
                    if (ma.googleAds?.project) setSelectedGoogleAdsProject(ma.googleAds.project);
                    if (ma.googleAds?.dataset) setSelectedGoogleAdsDataset(ma.googleAds.dataset);
                    if (ma.metaAds?.project) setSelectedMetaAdsProject(ma.metaAds.project);
                    if (ma.metaAds?.dataset) setSelectedMetaAdsDataset(ma.metaAds.dataset);
                    if (ma.metaAds?.table) setSelectedMetaAdsTable(ma.metaAds.table);
                    if (ma.crm?.project) setSelectedCrmProject(ma.crm.project);
                    if (ma.crm?.dataset) setSelectedCrmDataset(ma.crm.dataset);
                    if (ma.crm?.table) setSelectedCrmTable(ma.crm.table);
                }
                // Fallback to old format for backward compatibility
                if (!dataSources?.marketing_attribution && pipeline.dataSources?.ga4_export?.project) {
                    setSelectedGa4ExportProject(pipeline.dataSources.ga4_export.project);
                }
                if (!dataSources?.marketing_attribution && pipeline.dataSources?.ga4_export?.dataset) {
                    setSelectedGa4ExportDataset(pipeline.dataSources.ga4_export.dataset);
                }
            }
        } catch (error) {
            console.error('Failed to load pipeline:', error);
        }
    };

    const fetchBQProjects = useCallback(async () => {
        if (!selectedGBQConnection) return;
        setLoadingBQ(true);
        setBqError(null);
        try {
            const response = await apiFetch(`/api/connections/bigquery/projects?gbqConnectionId=${selectedGBQConnection}`);
            if (!response.ok) throw new Error('Failed to fetch projects');
            const data = await response.json();
            const projects = Array.isArray(data) ? data.map((p: any) =>
                typeof p === 'string' ? { id: p, name: p } : p
            ) : [];
            setBqProjects(projects);
        } catch (error: any) {
            setBqError(error.message);
        } finally {
            setLoadingBQ(false);
        }
    }, [selectedGBQConnection]);

    const fetchDatasetsForSource = useCallback(async (projectId: string, setDatasets: (ds: string[]) => void, setLoading: (l: boolean) => void) => {
        if (!selectedGBQConnection) return;
        setLoading(true);
        try {
            const response = await apiFetch(`/api/connections/bigquery/datasets?projectId=${projectId}&gbqConnectionId=${selectedGBQConnection}`);
            if (!response.ok) throw new Error('Failed to fetch datasets');
            const data = await response.json();
            const datasets = Array.isArray(data) ? data : [];
            setDatasets(datasets);
        } catch (error: any) {
            console.error('Error fetching datasets:', error);
        } finally {
            setLoading(false);
        }
    }, [selectedGBQConnection]);

    const fetchTablesForSource = useCallback(async (projectId: string, datasetId: string, setTables: (ts: string[]) => void, setLoading: (l: boolean) => void) => {
        if (!selectedGBQConnection) return;
        setLoading(true);
        try {
            const response = await apiFetch(`/api/connections/bigquery/tables?projectId=${projectId}&datasetId=${datasetId}&gbqConnectionId=${selectedGBQConnection}`);
            if (!response.ok) throw new Error('Failed to fetch tables');
            const data = await response.json();
            const tables = Array.isArray(data) ? data : [];
            setTables(tables);
        } catch (error: any) {
            console.error('Error fetching tables:', error);
        } finally {
            setLoading(false);
        }
    }, [selectedGBQConnection]);

    useEffect(() => {
        if (selectedGBQConnection) {
            fetchBQProjects();
        }
    }, [selectedGBQConnection, fetchBQProjects]);

    useEffect(() => {
        if (selectedGBQConnection && bqProjects.length > 0) {
            setGa4ExportProjects(bqProjects);
            setGoogleAdsProjects(bqProjects);
            setMetaAdsProjects(bqProjects);
            setCrmProjects(bqProjects);
        }
    }, [selectedGBQConnection, bqProjects]);

    useEffect(() => {
        if (selectedProject) {
            if (!selectedGa4ExportProject) setSelectedGa4ExportProject(selectedProject);
            if (!selectedGoogleAdsProject) setSelectedGoogleAdsProject(selectedProject);
            if (!selectedMetaAdsProject) setSelectedMetaAdsProject(selectedProject);
            if (!selectedCrmProject) setSelectedCrmProject(selectedProject);
        }
    }, [selectedProject, selectedGa4ExportProject, selectedGoogleAdsProject, selectedMetaAdsProject, selectedCrmProject]);

    useEffect(() => {
        if (selectedGa4ExportProject && selectedGBQConnection) {
            fetchDatasetsForSource(selectedGa4ExportProject, setGa4ExportDatasets, setLoadingGa4Export);
        }
    }, [selectedGa4ExportProject, selectedGBQConnection, fetchDatasetsForSource]);

    useEffect(() => {
        if (selectedGoogleAdsProject && selectedGBQConnection) {
            fetchDatasetsForSource(selectedGoogleAdsProject, setGoogleAdsDatasets, setLoadingGoogleAds);
        }
    }, [selectedGoogleAdsProject, selectedGBQConnection, fetchDatasetsForSource]);

    useEffect(() => {
        if (selectedMetaAdsProject && selectedGBQConnection) {
            fetchDatasetsForSource(selectedMetaAdsProject, setMetaAdsDatasets, setLoadingMetaAds);
        }
    }, [selectedMetaAdsProject, selectedGBQConnection, fetchDatasetsForSource]);

    useEffect(() => {
        if (selectedCrmProject && selectedGBQConnection) {
            fetchDatasetsForSource(selectedCrmProject, setCrmDatasets, setLoadingCrm);
        }
    }, [selectedCrmProject, selectedGBQConnection, fetchDatasetsForSource]);

    useEffect(() => {
        if (selectedMetaAdsProject && selectedMetaAdsDataset && selectedGBQConnection) {
            fetchTablesForSource(selectedMetaAdsProject, selectedMetaAdsDataset, setMetaAdsTables, setLoadingMetaAds);
        }
    }, [selectedMetaAdsProject, selectedMetaAdsDataset, selectedGBQConnection, fetchTablesForSource]);

    useEffect(() => {
        if (selectedCrmProject && selectedCrmDataset && selectedGBQConnection) {
            fetchTablesForSource(selectedCrmProject, selectedCrmDataset, setCrmTables, setLoadingCrm);
        }
    }, [selectedCrmProject, selectedCrmDataset, selectedGBQConnection, fetchTablesForSource]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        const pipelineData = {
            type: 'marketing-attribution' as const,
            name: pipelineName || 'Marketing Attribution Pipeline',
            connections: {
                gbqConnectionId: selectedGBQConnection,
                ...(selectedGoogleAdsConnection ? { gadsConnectionId: selectedGoogleAdsConnection } : {})
            },
            dataSources: {
                marketing_attribution: {
                    bigqueryConnector: selectedProject ? {
                        project: selectedProject
                    } : undefined,
                    ga4Export: selectedGa4ExportProject && selectedGa4ExportDataset ? {
                        project: selectedGa4ExportProject,
                        dataset: selectedGa4ExportDataset
                    } : undefined,
                    googleAds: selectedGoogleAdsProject && selectedGoogleAdsDataset ? {
                        project: selectedGoogleAdsProject,
                        dataset: selectedGoogleAdsDataset
                    } : undefined,
                    metaAds: selectedMetaAdsProject && selectedMetaAdsDataset && selectedMetaAdsTable ? {
                        project: selectedMetaAdsProject,
                        dataset: selectedMetaAdsDataset,
                        table: selectedMetaAdsTable
                    } : undefined,
                    crm: selectedCrmProject && selectedCrmDataset && selectedCrmTable ? {
                        project: selectedCrmProject,
                        dataset: selectedCrmDataset,
                        table: selectedCrmTable
                    } : undefined
                }
            },
            parameters: {
                attributionWindow
            }
        };

        try {
            const method = isEditing ? 'PUT' : 'POST';
            const url = isEditing ? `/api/pipelines/${id}` : '/api/pipelines';

            const res = await apiFetch(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(pipelineData)
            });

            if (res.ok) {
                navigate('/pipelines');
            } else {
                throw new Error('Failed to save pipeline');
            }
        } catch (error) {
            console.error('Error saving pipeline:', error);
            alert('Failed to save pipeline');
        }
    };

    return (
        <PipelineFormLayout isEditing={isEditing} pipelineType="marketing-attribution">
            <form onSubmit={handleSubmit}>
                {/* BigQuery Connector Block */}
                <div className="form-section">
                    <h3>BigQuery Connector</h3>
                    <div className="form-group">
                        <label>BigQuery Connection</label>
                        <select
                            value={selectedGBQConnection}
                            onChange={e => setSelectedGBQConnection(e.target.value)}
                            required
                        >
                            <option value="">Select Connection</option>
                            {connectionStatus?.gbq?.map(c => (
                                <option key={c.id} value={c.id}>{c.email}</option>
                            ))}
                        </select>
                    </div>
                    {selectedGBQConnection && (
                        <>
                            {bqError && <div className="error-message">{bqError}</div>}
                            <div className="form-group">
                                <label className="required">Project</label>
                                <select
                                    value={selectedProject}
                                    onChange={e => {
                                        setSelectedProject(e.target.value);
                                        if (!pipelineName && e.target.value && bqProjects.length > 0) {
                                            const project = bqProjects.find(p => p.id === e.target.value);
                                            if (project) {
                                                setPipelineName(project.name);
                                            }
                                        }
                                    }}
                                    required
                                    disabled={loadingBQ}
                                >
                                    <option value="">{loadingBQ ? 'Loading...' : 'Select Project'}</option>
                                    {bqProjects.map(p => (
                                        <option key={p.id} value={p.id}>{p.name} ({p.id})</option>
                                    ))}
                                </select>
                            </div>
                        </>
                    )}
                </div>

                {/* Data Sources for Marketing Attribution */}
                {selectedGBQConnection && (
                    <div className="form-section">
                        <h3>Data Sources</h3>
                        <p style={{ color: '#64748b', fontSize: '14px', marginTop: '-16px', marginBottom: '24px', paddingLeft: '16px' }}>
                            Select project and dataset for GA4 raw export, Google Ads, Meta Ads, and CRM data
                        </p>
                        {bqError && <div className="error-message">{bqError}</div>}
                        
                        {/* GA4 raw export */}
                        <div style={{ marginTop: '0', paddingTop: '0', borderTop: 'none' }}>
                            <h4 style={{ margin: 0, marginBottom: '16px', fontSize: '16px', fontWeight: '500', color: '#1a202c' }}>GA4 raw export dataset</h4>
                            <div className="form-group">
                                <label className="required">Project</label>
                                <select
                                    value={selectedGa4ExportProject}
                                    onChange={e => {
                                        setSelectedGa4ExportProject(e.target.value);
                                        setSelectedGa4ExportDataset('');
                                    }}
                                    required
                                    disabled={loadingBQ}
                                >
                                    <option value="">{loadingBQ ? 'Loading...' : 'Select Project'}</option>
                                    {ga4ExportProjects.map(p => (
                                        <option key={p.id} value={p.id}>{p.name} ({p.id})</option>
                                    ))}
                                </select>
                            </div>
                            {selectedGa4ExportProject && (
                                <div className="form-group">
                                    <label className="required">Dataset</label>
                                    <select
                                        value={selectedGa4ExportDataset}
                                        onChange={e => setSelectedGa4ExportDataset(e.target.value)}
                                        required
                                        disabled={loadingGa4Export || !selectedGa4ExportProject}
                                    >
                                        <option value="">{loadingGa4Export ? 'Loading...' : 'Select Dataset'}</option>
                                        {ga4ExportDatasets.map(d => (
                                            <option key={d} value={d}>{d}</option>
                                        ))}
                                    </select>
                                </div>
                            )}
                        </div>

                        {/* Google Ads */}
                        <div style={{ marginTop: '24px', paddingTop: '24px', borderTop: '1px solid #e2e8f0' }}>
                            <h4 style={{ margin: 0, marginBottom: '16px', fontSize: '16px', fontWeight: '500', color: '#1a202c' }}>Google Ads cost data export</h4>
                            <div className="form-group">
                                <label>Google Ads Connector</label>
                                <select
                                    value={selectedGoogleAdsConnection}
                                    onChange={e => setSelectedGoogleAdsConnection(e.target.value)}
                                >
                                    <option value="">Select Connection (optional)</option>
                                    {connectionStatus?.gads?.map(c => (
                                        <option key={c.id} value={c.id}>{c.email}</option>
                                    ))}
                                </select>
                                {(!connectionStatus?.gads || connectionStatus.gads.length === 0) && (
                                    <small style={{ color: '#64748b' }}>
                                        No Google Ads connections yet — add one in the Connections tab.
                                    </small>
                                )}
                            </div>
                            <div className="form-group">
                                <label>Project</label>
                                <select
                                    value={selectedGoogleAdsProject}
                                    onChange={e => {
                                        setSelectedGoogleAdsProject(e.target.value);
                                        setSelectedGoogleAdsDataset('');
                                    }}
                                    disabled={loadingBQ}
                                >
                                    <option value="">Select Project (optional)</option>
                                    {googleAdsProjects.map(p => (
                                        <option key={p.id} value={p.id}>{p.name} ({p.id})</option>
                                    ))}
                                </select>
                            </div>
                            {selectedGoogleAdsProject && (
                                <div className="form-group">
                                    <label>Dataset</label>
                                    <select
                                        value={selectedGoogleAdsDataset}
                                        onChange={e => setSelectedGoogleAdsDataset(e.target.value)}
                                        disabled={loadingGoogleAds || !selectedGoogleAdsProject}
                                    >
                                        <option value="">{loadingGoogleAds ? 'Loading...' : 'Select Dataset'}</option>
                                        {googleAdsDatasets.map(d => (
                                            <option key={d} value={d}>{d}</option>
                                        ))}
                                    </select>
                                </div>
                            )}
                        </div>

                        {/* Meta Ads */}
                        <div style={{ marginTop: '24px', paddingTop: '24px', borderTop: '1px solid #e2e8f0' }}>
                            <h4 style={{ margin: 0, marginBottom: '16px', fontSize: '16px', fontWeight: '500', color: '#1a202c' }}>Meta Ads cost data export tables</h4>
                            <div className="form-group">
                                <label>Project</label>
                                <select
                                    value={selectedMetaAdsProject}
                                    onChange={e => {
                                        setSelectedMetaAdsProject(e.target.value);
                                        setSelectedMetaAdsDataset('');
                                        setSelectedMetaAdsTable('');
                                    }}
                                    disabled={loadingBQ}
                                >
                                    <option value="">Select Project (optional)</option>
                                    {metaAdsProjects.map(p => (
                                        <option key={p.id} value={p.id}>{p.name} ({p.id})</option>
                                    ))}
                                </select>
                            </div>
                            {selectedMetaAdsProject && (
                                <>
                                    <div className="form-group">
                                        <label>Dataset</label>
                                        <select
                                            value={selectedMetaAdsDataset}
                                            onChange={e => {
                                                setSelectedMetaAdsDataset(e.target.value);
                                                setSelectedMetaAdsTable('');
                                            }}
                                            disabled={loadingMetaAds || !selectedMetaAdsProject}
                                        >
                                            <option value="">{loadingMetaAds ? 'Loading...' : 'Select Dataset'}</option>
                                            {metaAdsDatasets.map(d => (
                                                <option key={d} value={d}>{d}</option>
                                            ))}
                                        </select>
                                    </div>
                                    {selectedMetaAdsDataset && (
                                        <div className="form-group">
                                            <label>Table</label>
                                            <select
                                                value={selectedMetaAdsTable}
                                                onChange={e => setSelectedMetaAdsTable(e.target.value)}
                                                disabled={loadingMetaAds || !selectedMetaAdsDataset}
                                            >
                                                <option value="">{loadingMetaAds ? 'Loading...' : 'Select Table'}</option>
                                                {metaAdsTables.map(t => (
                                                    <option key={t} value={t}>{t}</option>
                                                ))}
                                            </select>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>

                        {/* CRM */}
                        <div style={{ marginTop: '24px', paddingTop: '24px', borderTop: '1px solid #e2e8f0' }}>
                            <h4 style={{ margin: 0, marginBottom: '16px', fontSize: '16px', fontWeight: '500', color: '#1a202c' }}>CRM data</h4>
                            <div className="form-group">
                                <label>Project</label>
                                <select
                                    value={selectedCrmProject}
                                    onChange={e => {
                                        setSelectedCrmProject(e.target.value);
                                        setSelectedCrmDataset('');
                                        setSelectedCrmTable('');
                                    }}
                                    disabled={loadingBQ}
                                >
                                    <option value="">Select Project (optional)</option>
                                    {crmProjects.map(p => (
                                        <option key={p.id} value={p.id}>{p.name} ({p.id})</option>
                                    ))}
                                </select>
                            </div>
                            {selectedCrmProject && (
                                <>
                                    <div className="form-group">
                                        <label>Dataset</label>
                                        <select
                                            value={selectedCrmDataset}
                                            onChange={e => {
                                                setSelectedCrmDataset(e.target.value);
                                                setSelectedCrmTable('');
                                            }}
                                            disabled={loadingCrm || !selectedCrmProject}
                                        >
                                            <option value="">{loadingCrm ? 'Loading...' : 'Select Dataset'}</option>
                                            {crmDatasets.map(d => (
                                                <option key={d} value={d}>{d}</option>
                                            ))}
                                        </select>
                                    </div>
                                    {selectedCrmDataset && (
                                        <div className="form-group">
                                            <label>Table</label>
                                            <select
                                                value={selectedCrmTable}
                                                onChange={e => setSelectedCrmTable(e.target.value)}
                                                disabled={loadingCrm || !selectedCrmDataset}
                                            >
                                                <option value="">{loadingCrm ? 'Loading...' : 'Select Table'}</option>
                                                {crmTables.map(t => (
                                                    <option key={t} value={t}>{t}</option>
                                                ))}
                                            </select>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                )}

                <div className="form-section">
                    <div className="form-group">
                        <label>Attribution window, days</label>
                        <input
                            type="number"
                            value={attributionWindow}
                            onChange={e => setAttributionWindow(parseInt(e.target.value) || 30)}
                            min="1"
                            placeholder="30"
                        />
                    </div>
                    <div className="form-group">
                        <label>Name</label>
                        <input
                            type="text"
                            value={pipelineName}
                            onChange={e => setPipelineName(e.target.value)}
                            placeholder="Pipeline Name"
                        />
                    </div>
                </div>

                <div className="form-actions">
                    <button type="submit" className="primary-button">Save Pipeline</button>
                </div>
            </form>
        </PipelineFormLayout>
    );
};

export default MarketingAttributionForm;
